/**
 * The message protocol between the Response Plan tab (the portal page) and
 * the plan page inside its sandboxed iframe. The plan page runs in an opaque
 * origin, so it cannot call the portal API itself. It posts these messages
 * to the parent; the parent checks them, makes the authenticated request,
 * and answers. The page side lives in the plan's build.py.
 *
 * Frame to parent:
 * - { action: 'ready' }: the page loaded; the parent answers with 'notes'.
 * - { action: 'height', height }: the page height, so the frame has no inner scroll bar.
 * - { action: 'send', requestId, sectionKey, body }: send one note.
 *
 * Parent to frame:
 * - { action: 'notes', notes, canSend }: every note on the plan.
 * - { action: 'sent', requestId, ok, error? }: the result of one send.
 */

export const PLAN_MESSAGE_TYPE = 'prentus-plan'
export const PLAN_MESSAGE_VERSION = 1
/** A plan section key (the page's data-note value). The Worker uses the same rule. */
export const PLAN_SECTION_KEY = /^[a-z0-9][a-z0-9-]{0,79}$/
const REQUEST_ID = /^[A-Za-z0-9_-]{1,64}$/
/** The longest note a customer can send. The Worker uses the same limit. */
export const PLAN_NOTE_MAX_CHARS = 2_000

export type PlanFrameMessage =
  | { action: 'ready' }
  | { action: 'height'; height: number }
  | { action: 'send'; requestId: string; sectionKey: string; body: string }
  /** A send the parent refuses before any request; the parent still answers it. */
  | { action: 'invalid'; requestId: string; error: string }

/** Returns the message when it is a valid plan message, else null. Anything else on the window is ignored. */
export function parsePlanFrameMessage(data: unknown): PlanFrameMessage | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  const value = data as Record<string, unknown>
  if (value.type !== PLAN_MESSAGE_TYPE || value.v !== PLAN_MESSAGE_VERSION) return null
  if (value.action === 'ready') return { action: 'ready' }
  if (value.action === 'height') {
    const height = Number(value.height)
    return Number.isFinite(height) && height > 0 ? { action: 'height', height } : null
  }
  if (value.action === 'send') {
    if (typeof value.requestId !== 'string' || !REQUEST_ID.test(value.requestId)) return null
    if (typeof value.sectionKey !== 'string' || !PLAN_SECTION_KEY.test(value.sectionKey)) return { action: 'invalid', requestId: value.requestId, error: 'That part of the plan does not take notes.' }
    const body = typeof value.body === 'string' ? value.body.trim() : ''
    if (!body) return { action: 'invalid', requestId: value.requestId, error: 'Write a note before you send it.' }
    if (body.length > PLAN_NOTE_MAX_CHARS) return { action: 'invalid', requestId: value.requestId, error: 'Keep the note under 2,000 characters.' }
    return { action: 'send', requestId: value.requestId, sectionKey: value.sectionKey, body }
  }
  return null
}

/** The frame height in pixels, bounded so a bad value cannot collapse or flood the page. */
export function clampPlanHeight(height: number): number {
  return Math.min(40_000, Math.max(480, Math.ceil(height)))
}

export function planMessage<T extends Record<string, unknown>>(payload: T): T & { type: typeof PLAN_MESSAGE_TYPE; v: typeof PLAN_MESSAGE_VERSION } {
  return { type: PLAN_MESSAGE_TYPE, v: PLAN_MESSAGE_VERSION, ...payload }
}
